import type MainProject from '@/model/MainProject'
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import EventTextChoice from '@/model/EventClass/EventTextChoice'

function getPureText(text = ''): string {
  return text
    .replace(/\|n/g, '')
    .replace(/\\n/g, '')
    .replace(/\\c\[\d+,\d+,\d+]/gi, '')
    .replace(/\\\|/g, '')
    .replace(/\\\./g, '')
    .replace(/\\\>/g, '')
    .replace(/\\\=/g, '')
    .trim()
}

function getTextChoiceContent(event: EventBase): string {
  if (event.Code !== EventID.TextChoice) return ''
  if (event instanceof EventTextChoice) {
    return String(event.Content ?? '').trim()
  }
  return String(event.Parameter?.[1] ?? '').trim()
}

/**
 * 统计单条文字选项字数。
 * 编辑器内 Parameter[1] 为纯选项文案；旧版/导入可能为 `：[选项] 的剧情` 格式，取 `[]` 内文本。
 */
function countTextChoiceWords(content: string): number {
  const text = String(content ?? '').trim()
  if (!text) return 0
  const endIndex = text.lastIndexOf(']')
  const startIndex = endIndex >= 0 ? text.lastIndexOf('[', endIndex) : -1
  if (startIndex >= 0 && endIndex > startIndex) {
    return text.slice(startIndex + 1, endIndex).length
  }
  return text.length
}

/**
 * 统计事件列表中的发布字数。
 * 口径与发布参数 `word` 保持一致，只统计文本、文字选项和气泡文本。
 *
 * @param events 剧情事件列表。
 * @returns 当前事件列表字数。
 */
export function countEventWords(events: EventBase[]): number {
  let total = 0
  for (const event of events) {
    const raw = event as unknown as {
      Code?: number
      RoleName?: string
      Content?: string
      MessageType?: number
      MessageConten?: string
    }
    switch (raw.Code) {
      case EventID.Text:
        total += String(raw.RoleName ?? '').trim().length
        total += getPureText(String(raw.Content ?? '')).length
        break
      case EventID.TextChoice:
        total += countTextChoiceWords(getTextChoiceContent(event))
        break
      case EventID.SendAirBubble:
        if (Number(raw.MessageType ?? 0) === 1) {
          total += getPureText(String(raw.MessageConten ?? '')).length
        }
        break
      default:
        break
    }
  }
  return total
}

/** 按剧情 ID 缓存的字数表。 */
export type ProjectWordCountByMap = Map<number, number>

/**
 * 按剧情统计字数并写入 Map 缓存。
 *
 * @param project 当前工程；为空时返回空 Map 与 total 0。
 * @returns 各剧情字数与工程总字数。
 */
export function buildProjectWordCountByMap(project: MainProject | null | undefined): {
  byMapId: ProjectWordCountByMap
  total: number
} {
  const byMapId: ProjectWordCountByMap = new Map()
  let total = 0
  for (const map of project?.storyTree.Maps ?? []) {
    const count = countEventWords(map.List)
    byMapId.set(map.Id, count)
    total += count
  }
  return { byMapId, total }
}

/**
 * 统计整个工程的发布字数（不构建按剧情缓存）。
 *
 * @param project 当前工程；为空时返回 0。
 * @returns 当前工程全部剧情的字数。
 */
export function countProjectWords(project: MainProject | null | undefined): number {
  return buildProjectWordCountByMap(project).total
}
